/**
 * 工作区元素选中 / 悬停 UI（世界坐标，与页内 InteractionLayer 对齐）。
 */
import { useMemo } from 'react';
import type { Element, LineElement, TextElement } from '@/components/canvas/document/publicTypes';
import { HandleLayer, SelectionBoxLayer } from '@/components/canvas/selection';
import type { ResizeHandle } from '@/components/canvas/transform';
import { getReferenceTextFontSize } from '@/components/canvas/elements/text/model/textResize';
import { getLineChromeRect, getLineEndOffset } from '@/components/canvas/elements/shape/line/lineElementPatch';
import type { WorkspaceInteractionApi } from '@/components/canvas/workspace/controllers/useWorkspaceInteraction';

export interface WorkspaceElementSelectionOverlayProps {
  interaction: WorkspaceInteractionApi;
  workspaceElements: Element[];
  selectedElementIds: string[];
  hoveredElementId: string | null;
  zoom: number;
  hideSelectionChrome: boolean;
  /** 多选 / 跨页选区时由 WorldSelectionOverlay 绘制联合选框 */
  useWorldSelectionChrome?: boolean;
  dragOffset?: { dx: number; dy: number };
}

export function WorkspaceElementSelectionOverlay({
  interaction,
  workspaceElements,
  selectedElementIds,
  hoveredElementId,
  zoom,
  hideSelectionChrome,
  useWorldSelectionChrome = false,
  dragOffset = { dx: 0, dy: 0 },
}: WorkspaceElementSelectionOverlayProps) {
  const {
    resizeState,
    resizePreview,
    pushHistory,
    setResizeState,
    setResizePreview,
    resizePreviewRef,
    lineHandleState,
    lineHandlePreview,
    setLineHandleState,
    setLineHandlePreview,
  } = interaction;

  const selected = useMemo(
    () =>
      workspaceElements.filter(
        (el) => selectedElementIds.includes(el.id) && el.type !== 'line' && !el.layout.parentId,
      ),
    [workspaceElements, selectedElementIds],
  );

  const hoverEls = useMemo(() => {
    if (!hoveredElementId || hideSelectionChrome) return [];
    const el = workspaceElements.find((e) => e.id === hoveredElementId);
    if (!el || el.interaction.locked || el.type === 'line' || el.layout.parentId) return [];
    if (selectedElementIds.includes(el.id)) return [];
    return [el];
  }, [workspaceElements, hoveredElementId, hideSelectionChrome, selectedElementIds]);

  const editingTextId = interaction.editingTextId;
  const single =
    selected.length === 1 && selected[0]!.id !== editingTextId ? selected[0]! : null;

  const singleLine = useMemo(() => {
    if (selectedElementIds.length !== 1 || hideSelectionChrome) return null;
    const el = workspaceElements.find((e) => e.id === selectedElementIds[0]);
    if (!el || el.type !== 'line' || el.layout.parentId) return null;
    return el as LineElement;
  }, [workspaceElements, selectedElementIds, hideSelectionChrome]);

  const startResize = (el: Element, handle: ResizeHandle, e: React.MouseEvent) => {
    if (el.interaction.locked) return;
    e.stopPropagation();
    e.preventDefault();
    pushHistory();
    const base =
      resizeState?.id === el.id && resizePreview
        ? resizePreview
        : {
            x: el.layout.x,
            y: el.layout.y,
            width: el.size.width,
            height: el.size.height,
          };
    setResizeState({
      id: el.id,
      handle,
      startX: base.x,
      startY: base.y,
      startW: base.width,
      startH: base.height,
      startClientX: e.clientX,
      startClientY: e.clientY,
      ...(el.type === 'text'
        ? {
            startFontSize: getReferenceTextFontSize(el as TextElement),
            startTypography: { ...(el as TextElement).typography },
            startDocument: (el as TextElement).data.document,
          }
        : {}),
    });
    setResizePreview({ x: base.x, y: base.y, width: base.width, height: base.height });
    resizePreviewRef.current = { x: base.x, y: base.y, width: base.width, height: base.height };
  };

  if (hideSelectionChrome) return null;
  if (selected.length === 0 && hoverEls.length === 0 && !singleLine) return null;

  const noop = () => {};

  const singleRect = single
    ? resizeState?.id === single.id && resizePreview
      ? {
          x: resizePreview.x + dragOffset.dx,
          y: resizePreview.y + dragOffset.dy,
          width: resizePreview.width,
          height: resizePreview.height,
        }
      : {
          x: single.layout.x + dragOffset.dx,
          y: single.layout.y + dragOffset.dy,
          width: single.size.width,
          height: single.size.height,
        }
    : null;

  const lineChrome = singleLine
    ? (() => {
        const offset = { dx: dragOffset.dx, dy: dragOffset.dy };
        if (lineHandleState?.id === singleLine.id && lineHandlePreview) {
          return getLineChromeRect(
            {
              ...singleLine,
              layout: { ...singleLine.layout, x: lineHandlePreview.x, y: lineHandlePreview.y },
              data: { start: { x: 0, y: 0 }, end: { x: lineHandlePreview.endX, y: lineHandlePreview.endY } },
              size: {
                width: Math.max(1, Math.abs(lineHandlePreview.endX)),
                height: Math.max(1, Math.abs(lineHandlePreview.endY)),
              },
            },
            offset,
          );
        }
        return getLineChromeRect(singleLine, offset);
      })()
    : null;

  const lineEnd = singleLine
    ? lineHandleState?.id === singleLine.id && lineHandlePreview
      ? { endX: lineHandlePreview.endX, endY: lineHandlePreview.endY }
      : getLineEndOffset(singleLine)
    : { endX: 0, endY: 0 };

  const startLineHandle = (handle: 'start' | 'end', e: React.MouseEvent) => {
    if (!singleLine || singleLine.interaction.locked) return;
    e.stopPropagation();
    e.preventDefault();
    pushHistory();
    const { endX, endY } = getLineEndOffset(singleLine);
    setLineHandleState({
      id: singleLine.id,
      handle,
      coordinateSpace: 'workspace',
      startX: singleLine.layout.x,
      startY: singleLine.layout.y,
      startEndX: endX,
      startEndY: endY,
      startClientX: e.clientX,
      startClientY: e.clientY,
    });
    setLineHandlePreview({
      x: singleLine.layout.x,
      y: singleLine.layout.y,
      endX,
      endY,
    });
  };

  return (
    <div
      className="absolute inset-0"
      style={{ zIndex: 1100 }}
      data-workspace-selection-overlay
      aria-hidden
    >
      {hoverEls.length > 0 && (
        <SelectionBoxLayer elements={hoverEls} dragOffset={{ dx: 0, dy: 0 }} zoom={zoom} />
      )}
      {selected.length > 0 && !useWorldSelectionChrome && (
        <SelectionBoxLayer elements={selected} dragOffset={dragOffset} zoom={zoom} />
      )}
      {single && singleRect && (
        <HandleLayer
          element={single}
          rect={singleRect}
          zoom={zoom}
          isLine={false}
          lineEndX={0}
          lineEndY={0}
          currentRadius={0}
          showRotate={false}
          showRadiusHandles={single.type === 'rect'}
          resizeHandles={undefined}
          onStartResize={(handle, e) => startResize(single, handle, e)}
          onStartLineHandle={noop}
          onStartRotate={noop}
          onStartRadiusDrag={noop}
        />
      )}
      {singleLine && lineChrome && (
        <HandleLayer
          element={singleLine}
          rect={lineChrome}
          zoom={zoom}
          isLine
          lineEndX={lineEnd.endX}
          lineEndY={lineEnd.endY}
          currentRadius={0}
          showRotate={false}
          showRadiusHandles={false}
          onStartResize={noop}
          onStartLineHandle={startLineHandle}
          onStartRotate={noop}
          onStartRadiusDrag={noop}
        />
      )}
    </div>
  );
}
